import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AlertTriangle, ExternalLink, Loader2, Settings2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, timeAgo } from "@/lib/format";

/**
 * 99A — shows ONLY when the stored Xero refresh token has died (`/xero/status.needs_reconnect`),
 * i.e. the hourly bill + payment sweeps are silently stalled. Admins can reconnect right here
 * (Xero opens in a new tab and brings them back to this page); everyone else is told who to ask.
 *
 * `scope` tunes the copy: "bills" (Supplier bills register) or "forecast" (Cashflow forecast).
 * 106B — `variant="strip"` is the slim app-wide version mounted in the shell for admins only (dismissable per session).
 */
const STRIP_DISMISS_KEY = "bf_xero_strip_dismissed";

export const XeroReconnectBanner = ({ scope = "bills", className = "", variant = "card" }) => {
  const { user } = useAuth();
  const location = useLocation();
  const isAdmin = user?.role === "admin";
  const isStrip = variant === "strip";
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(() => isStrip && sessionStorage.getItem(STRIP_DISMISS_KEY) === "1");
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    if (isStrip && !isAdmin) return;           // the strip is admin-only; the page banners tell everyone else who to ask
    try {
      const res = await api.get("/xero/status");
      setStatus(res.data || null);
    } catch {
      setStatus(null); // not an editor / not configured — nothing to show
    }
  }, [isStrip, isAdmin]);

  useEffect(() => { load(); return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, [load]);
  // the strip re-checks when the token dies / comes back while the app stays open
  useEffect(() => {
    if (!isStrip || !isAdmin) return undefined;
    const t = setInterval(load, 10 * 60 * 1000);
    return () => clearInterval(t);
  }, [isStrip, isAdmin, load]);

  if (!status?.needs_reconnect) return null;
  if (isStrip && (dismissed || !isAdmin || location.pathname.startsWith("/settings"))) return null;

  const since = status.refresh_failed_at || "";
  const lastBills = status.bills_last_pull_at || "";
  const returnTo = `${location.pathname}${location.search || ""}`;

  const reconnect = async () => {
    setBusy(true);
    try {
      const res = await api.get("/xero/connect-url", { params: { return_to: returnTo } });
      // Xero refuses to load inside an iframe (preview frame) — always a new tab
      const w = window.open(res.data.url, "_blank", "noopener");
      if (!w) { window.location.href = res.data.url; return; }
      toast.info("Finish the Xero login in the new tab — this page updates itself once reconnected");
      let tries = 0;
      pollRef.current = setInterval(async () => {
        tries += 1;
        try {
          const st = await api.get("/xero/status");
          if (st.data.connected && !st.data.needs_reconnect) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setStatus(st.data);
            setBusy(false);
            toast.success("Xero reconnected — bill and payment sync resumes on the next sweep");
            return;
          }
        } catch { /* keep polling */ }
        if (tries > 60) { clearInterval(pollRef.current); pollRef.current = null; setBusy(false); }
      }, 4000);
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  const what = scope === "forecast"
    ? "The forecast is working from stale supplier bills and payments"
    : "Supplier bill and payment statuses are no longer updating";

  if (isStrip) {
    return (
      <div
        role="alert"
        className={`z-[45] flex items-center gap-3 border-b border-amber-500/40 bg-amber-500/10 px-4 py-1.5 text-amber-100 sm:px-6 lg:px-8 ${className}`}
        data-testid="xero-reconnect-strip"
      >
        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-300" />
        <p className="min-w-0 flex-1 truncate text-xs">
          <span className="font-semibold text-amber-200">Xero disconnected</span>
          <span className="text-amber-200/80">{since ? ` since ${fmtDate(since)}` : ""} — supplier bills, payment statuses and the cashflow forecast are not updating until you reconnect.</span>
        </p>
        <Button size="sm" className="h-7 text-xs" onClick={reconnect} disabled={busy} data-testid="xero-reconnect-strip-button">
          {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="mr-1 h-3.5 w-3.5" />} Reconnect
        </Button>
        <Button asChild size="sm" variant="ghost" className="h-7 text-xs text-amber-200 hover:text-amber-100">
          <Link to="/settings?tab=xero" data-testid="xero-reconnect-strip-settings">Settings</Link>
        </Button>
        <button
          type="button"
          aria-label="Hide for this session"
          className="rounded p-1 text-amber-200/70 transition-colors hover:text-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => { sessionStorage.setItem(STRIP_DISMISS_KEY, "1"); setDismissed(true); }}
          data-testid="xero-reconnect-strip-dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div
      role="alert"
      className={`flex flex-col gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 md:flex-row md:items-center md:justify-between ${className}`}
      data-testid="xero-reconnect-banner"
      data-scope={scope}
    >
      <div className="flex min-w-0 items-start gap-3">
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-500/20 text-amber-300">
          <AlertTriangle className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-amber-200" data-testid="xero-reconnect-title">Xero sync has stalled — reconnect to resume</p>
          <p className="mt-0.5 text-xs text-amber-200/80" data-testid="xero-reconnect-detail">
            {what}
            {since ? ` — the connection expired ${timeAgo(since)} (${fmtDate(since)})` : ""}
            {lastBills ? ` · bills last synced ${fmtDate(lastBills)}` : ""}.
            {!isAdmin && " Ask an admin to reconnect in Settings → Xero."}
          </p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2 md:pl-3">
        {isAdmin && (
          <Button size="sm" className="h-8 text-xs" onClick={reconnect} disabled={busy} data-testid="xero-reconnect-button">
            {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="mr-1 h-3.5 w-3.5" />} Reconnect Xero
          </Button>
        )}
        <Button asChild size="sm" variant="outline" className="h-8 text-xs">
          <Link to="/settings?tab=xero" data-testid="xero-reconnect-settings-link"><Settings2 className="mr-1 h-3.5 w-3.5" /> Open settings</Link>
        </Button>
      </div>
    </div>
  );
};
